import type { ReactNode } from 'react'

import { Card, CardBody, CardHeader } from '@/src/elements/card'
import { Typography } from '@/src/elements/text/Typography'
import type { TCommunityApiHasJoinedInfos, TMediumCommunityApi } from '@/src/features/community/api/communityApi.types'
import { CommunityThumbnail } from '@/src/features/community/ui/components/CommunityThumbnail'
import { createDatesSentence } from '@/src/general/primitives/dates/createDatesSentence'
import { getImageUrl } from '@/src/general/utils/getImageUrl'
import { useTranslate } from '@/src/lang/useTranslate'

import { SettingsCardSwitchInput } from '../SettingsCardSwitchInput'

export type TSettingsCardCommunitiesBaseProps = {
  community: Pick<TMediumCommunityApi, 'name'> &
    Partial<Pick<TMediumCommunityApi, 'picture' | 'lifetime' | 'fromDate' | 'toDate'>> &
    Pick<TCommunityApiHasJoinedInfos, 'subscription'>
  isPublicationByEmailEnabled?: boolean
  isPublicationByPushEnabled?: boolean
  isAnnouncementByEmailEnabled?: boolean
  publicationByEmailDisabled?: boolean
  publicationByPushDisabled?: boolean
  announcementByEmailDisabled?: boolean
  onPublicationByEmailChange?: (value: boolean) => Promise<void> | void
  onPublicationByPushChange?: (value: boolean) => Promise<void> | void
  onAnnouncementByEmailChange?: (value: boolean) => Promise<void> | void
  testID?: string
}

export const SettingsCardCommunitiesBase = ({
  testID,
  community,
  isAnnouncementByEmailEnabled,
  isPublicationByEmailEnabled,
  isPublicationByPushEnabled,
  publicationByEmailDisabled,
  publicationByPushDisabled,
  announcementByEmailDisabled,
  onAnnouncementByEmailChange,
  onPublicationByEmailChange,
  onPublicationByPushChange,
}: TSettingsCardCommunitiesBaseProps): ReactNode => {
  const { t } = useTranslate()

  const subtitle = () => {
    if (!community.subscription?.fromDate || !community?.subscription.toDate) return undefined

    const [fromDate, toDate] = createDatesSentence(
      community.subscription.fromDate || '',
      community.subscription.toDate,
      'short',
    )
    return t('community.date.from-to', { fromDate, toDate })
  }

  return (
    <Card testID={testID} className="gap-2 p-3">
      <CardHeader className="flex flex-col gap-1 p-1">
        <CommunityThumbnail
          title={community.name}
          showImage
          bold
          imgSource={community.picture && getImageUrl(community.picture)}
          subtitle={subtitle()}
        />
      </CardHeader>

      <CardBody className="flex flex-col gap-2 p-1">
        <Typography size="sm" bold contrast="low" ariaRole="span">
          {t('settings.card.community.prayer')}
        </Typography>
        <SettingsCardSwitchInput.Mail
          value={isPublicationByEmailEnabled}
          onValueChange={onPublicationByEmailChange}
          disabled={publicationByEmailDisabled}
        />
        <SettingsCardSwitchInput.Push
          value={isPublicationByPushEnabled}
          onValueChange={onPublicationByPushChange}
          disabled={publicationByPushDisabled}
        />
        <Typography size="sm" bold contrast="low" ariaRole="span">
          {t('settings.card.community.announce')}
        </Typography>
        <Typography size="xs" contrast="low" ariaRole="span">
          {t('settings.card.community.announce.details')}
        </Typography>

        <SettingsCardSwitchInput.Mail
          value={isAnnouncementByEmailEnabled}
          onValueChange={onAnnouncementByEmailChange}
          disabled={announcementByEmailDisabled}
        />
      </CardBody>
    </Card>
  )
}
